import {
  Required,
  Schema,
  TextField,
  RemoteDataSource,
  EnumField,
  ForeignKeyField,
  DynamicColumn
} from "lego-gui-lib/lib";
import { mainStore, api } from "../../core/mainStore";
import { CustomerFK } from "../customer/schema";
import get from "lodash/get";
import { FormState } from "final-form";
import {
  customerChanged,
  customerInit,
  onVisibilityChange,
  onVisibilityInit,
  VisibilityEnum,
  VisibilityType,
  withVisibility
} from "../../common/common";
import { SANFK, SANFKOutOfContext } from "../san/schema";
import { t } from "../../core/i18n/i18n";
import { Auth } from "../../core/model/auth";
import { ContextAwareListDataSource } from "../../common/ContextAwareListDataSource";
import { createContext } from "react";
import { AccessContext } from "lego-gui-lib";

const auth = mainStore.auth;

export enum ListType {
  IN = 10,
  IN_BLACK = 0,
  IN_WHITE = 1,
  OUT = 20,
  OUT_BLACK = 2,
  OUT_WHITE = 3,
  EXCEPTION = 4,
  CUSTOM = 5
}

export const TypeTypes = [
  { label: t("hns:list:type.inBlack"), value: ListType.IN_BLACK },
  { label: t("hns:list:type.inWhite"), value: ListType.IN_WHITE },
  { label: t("hns:list:type.outBlack"), value: ListType.OUT_BLACK },
  { label: t("hns:list:type.outWhite"), value: ListType.OUT_WHITE },
  { label: t("hns:list:type.exception"), value: ListType.EXCEPTION },
  { label: t("hns:list:type.custom"), value: ListType.CUSTOM }
];

export const ListSchema = new Schema<DynamicColumn>({
  fields: [
    {
      name: "name",
      title: t("common:name"),
      type: TextField(Required)
    },
    {
      name: "visibility",
      title: t("common:visibility"),
      type: VisibilityEnum(),
      readonly: () => !auth.isAdmin(),
      onChange: onVisibilityChange,
      onInit: onVisibilityInit
    },
    {
      name: "customerRef",
      title: t("hns:customer:domain"),
      type: CustomerFK,
      if: () => auth.isAdmin(),
      onChange: customerChanged,
      onInit: customerInit
    },
    {
      name: "sanRef",
      title: t("hns:san:domain"),
      type: () => (auth.isAnyCustomerOwner() ? SANFK : SANFKOutOfContext)
    },
    {
      name: "type",
      title: t("common:type"),
      type: EnumField(TypeTypes, Required)
    }
  ],
  primaryKey: ["id"],
  defaultSort: { sort: "visibility", order: "desc" },
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ANYONE_WITH_WRITE
  }
});

export const CreateListSchema = ListSchema.extend({
  visibility: {
    type: VisibilityEnum(),
    onChange: (
      newValue: VisibilityType,
      oldValue: VisibilityType,
      formState: FormState<any>
    ) => {
      onVisibilityChange(newValue, oldValue, formState);
      if (newValue === VisibilityType.PUBLIC) {
        formState.values["sanRef"] = null;
      }
    },
    onInit: newValue => {
      onVisibilityInit(newValue);
      if (newValue === VisibilityType.PUBLIC) {
        mainStore.SAN.oneDataSource.clear();
      }
    }
  },
  customerRef: {
    if: (ctx: AccessContext) =>
      auth.isAdmin() && get(ctx, "data.visibility") === VisibilityType.PRIVATE
  },
  sanRef: {
    if: (ctx: AccessContext) =>
      get(ctx, "data.visibility") === VisibilityType.PRIVATE,
    type: () => SANFK
  }
});

export const UpdateListSchema = CreateListSchema.extend({
  visibility: { readonly: true },
  type: { readonly: true },
  customerRef: { readonly: true },
  sanRef: { readonly: true, type: () => SANFK }
});

export const ReadonlyUpdateListSchema = UpdateListSchema.makeAllReadonly();

mainStore.list = RemoteDataSource.create({
  api: api,
  name: "list",
  schema: ListSchema,
  url: "/lists",
  commons: mainStore.commons
});

export const listContext = createContext(mainStore.list);

mainStore.customerLists = new ContextAwareListDataSource({
  api: api,
  name: "list",
  schema: ListSchema,
  url: "/lists",
  commons: mainStore.commons,
  observableFieldRef: "customerRef",
  observableDataSource: "customer"
});

export const customerListsContext = createContext(mainStore.customerLists);

mainStore.customerList = ContextAwareListDataSource.clone(
  mainStore.list.listDataSource,
  {
    observableFieldRef: "sanRef",
    observableDataSource: "SAN"
  }
);

mainStore.listFk = ContextAwareListDataSource.clone(mainStore.customerList);

export const ListFK = ForeignKeyField({
  dataSource: mainStore.listFk,
  searchField: "name",
  labelField: withVisibility(),
  valueField: "id",
  minWidth: 70
});

export const ListFKDataSource = ForeignKeyField({
  dataSource: mainStore.list.listDataSource,
  searchField: "name",
  labelField: withVisibility(),
  valueField: "id",
  minWidth: 70
});

mainStore.listFkIn = ContextAwareListDataSource.clone(mainStore.listFk, {
  params: {
    filters: [
      {
        field: "type",
        value: ListType.IN.toString()
      }
    ]
  }
});

export const ListFKPrimaryIn = ForeignKeyField({
  dataSource: mainStore.listFkIn,
  searchField: "name",
  labelField: withVisibility(),
  valueField: "id",
  minWidth: 70
});

mainStore.listFkOut = ContextAwareListDataSource.clone(mainStore.listFk, {
  params: {
    filters: [
      {
        field: "type",
        value: ListType.OUT.toString()
      }
    ]
  }
});

export const ListFKPrimaryOut = ForeignKeyField({
  dataSource: mainStore.listFkOut,
  searchField: "name",
  labelField: withVisibility(),
  valueField: "id",
  minWidth: 70
});

mainStore.listFkException = ContextAwareListDataSource.clone(mainStore.listFk, {
  params: {
    filters: [
      {
        field: "type",
        value: ListType.EXCEPTION.toString()
      }
    ]
  }
});

export const ListFKException = ForeignKeyField({
  dataSource: mainStore.listFkException,
  searchField: "name",
  labelField: withVisibility(),
  valueField: "id",
  minWidth: 70
});

mainStore.listFkCustom = ContextAwareListDataSource.clone(mainStore.listFk, {
  params: {
    filters: [
      {
        field: "type",
        value: ListType.CUSTOM.toString()
      }
    ]
  }
});

export const ListFKCustom = ForeignKeyField({
  dataSource: mainStore.listFkCustom,
  searchField: "name",
  labelField: withVisibility(),
  valueField: "id",
  minWidth: 70
});
